import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronUp, Search } from 'lucide-react';
import { usePortfolios } from '../hooks/usePortfolios';
import { useSites } from '../hooks/useSites';
import { useHeartbeatSubscription } from '../hooks/useHeartbeatSubscription';
import { useFleetTelemetry } from '../hooks/useFleetTelemetry';
import { useDashboardStore } from '../store/dashboardStore';
import { PortfolioSummaryCard } from '../components/PortfolioSummaryCard';
import { SiteGrid } from '../components/SiteGrid';
import { PortfolioSummaryPanel } from '../components/portfolio/PortfolioSummaryPanel';
import { PortfolioAggregateData } from '../types/portfolio.types';
import api from '../services/api';

export function PortfolioDashboard() {
  const navigate = useNavigate();
  const [showSummary, setShowSummary] = useState(false);

  useHeartbeatSubscription();

  const {
    selectedPortfolioId,
    selectedStatus,
    searchQuery,
    currentPage,
    setPortfolioFilter,
    setSearchQuery,
  } = useDashboardStore();

  const { data: portfolios, isLoading: portfoliosLoading } = usePortfolios();

  const { data: sitesData, isLoading: sitesLoading } = useSites({
    portfolio_id: selectedPortfolioId,
    status: selectedStatus,
    search: searchQuery,
    page: currentPage,
    limit: 50,
  });

  const siteIds = useMemo(
    () => (sitesData?.sites ?? []).map((s) => s.site_id),
    [sitesData]
  );
  const { liveBySite, nowMs } = useFleetTelemetry(siteIds);

  const { data: aggregateData, isLoading: isAggregateLoading } = useQuery({
    queryKey: ['portfolio-aggregates', selectedPortfolioId, selectedStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedPortfolioId) params.append('portfolio_id', selectedPortfolioId);
      if (selectedStatus) params.append('status', selectedStatus);
      const response = await api.get<{ success: boolean; data: PortfolioAggregateData }>(
        `/v1/portfolios/aggregate-kpis?${params.toString()}`
      );
      return response.data.data;
    },
    refetchInterval: 15000,
  });

  const handleSiteClick = useCallback((siteId: string) => {
    navigate(`/project/${siteId}`);
  }, [navigate]);

  const handlePortfolioClick = (portfolioId: string) => {
    setPortfolioFilter(selectedPortfolioId === portfolioId ? undefined : portfolioId);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Fleet Summary Bar */}
      {aggregateData && (
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-3 md:p-5">
          <div className="flex items-center gap-3 md:gap-8 overflow-x-auto scrollbar-hide">
            <SummaryItem label="Total Sites" value={aggregateData.fleet_overview.total_sites} />
            <SummaryItem label="Online" value={aggregateData.fleet_overview.online_sites} color="text-status-online" />
            <SummaryItem label="Degraded" value={aggregateData.fleet_overview.degraded_sites ?? 0} color="text-status-degraded" />
            <SummaryItem
              label="Total Capacity"
              value={`${Number(aggregateData.fleet_overview.total_capacity_mwh).toFixed(1)} MWh`}
            />
            {aggregateData.battery_storage_fleet && (
              <SummaryItem
                label="Avg SOC"
                value={`${Number(aggregateData.battery_storage_fleet.avg_state_of_charge ?? 0).toFixed(0)}%`}
              />
            )}
            <div className="w-px h-8 bg-ems-border flex-shrink-0 hidden md:block" />
            <SummaryItem
              label="Total Load"
              value={
                aggregateData.fleet_overview.total_load_kw != null
                  ? `${(Number(aggregateData.fleet_overview.total_load_kw) / 1000).toFixed(1)} MW`
                  : '--'
              }
            />
            <SummaryItem
              label="System Health"
              value={
                aggregateData.fleet_overview.total_sites > 0
                  ? `${Math.round((aggregateData.fleet_overview.online_sites / aggregateData.fleet_overview.total_sites) * 100)}%`
                  : '--'
              }
              color={
                aggregateData.fleet_overview.total_sites > 0
                  ? (aggregateData.fleet_overview.online_sites / aggregateData.fleet_overview.total_sites) >= 0.9
                    ? 'text-status-online'
                    : (aggregateData.fleet_overview.online_sites / aggregateData.fleet_overview.total_sites) >= 0.7
                      ? 'text-status-degraded'
                      : 'text-status-critical'
                  : undefined
              }
            />
            <SummaryItem
              label="Fleet Fuel"
              value={
                aggregateData.fleet_overview.fuel_consumption_mmbtu_h != null
                  ? `${Number(aggregateData.fleet_overview.fuel_consumption_mmbtu_h).toFixed(1)} MMBtu/h`
                  : '--'
              }
            />
          </div>
        </div>
      )}

      {/* Body: left column (Summary + Region) | right column (Sites) */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left column — Portfolio Summary (desktop) + Filter by Region */}
        <aside className="w-full md:w-[360px] md:flex-shrink-0 space-y-6">
          {/* Desktop summary panel — mobile uses the bottom sheet below */}
          {aggregateData && (
            <div className="hidden md:block">
              <PortfolioSummaryPanel data={aggregateData} isLoading={isAggregateLoading} />
            </div>
          )}

          {/* Portfolio Filter Cards */}
          <section>
            <h2 className="text-sm font-semibold text-ems-text-secondary uppercase tracking-wider mb-3">Filter by Region</h2>
            {portfoliosLoading ? (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide md:grid md:grid-cols-1 md:gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 min-w-[160px] md:min-w-0 rounded-2xl animate-pulse bg-ems-bg-card" />
                ))}
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto scrollbar-hide md:grid md:grid-cols-1 md:gap-3">
                {portfolios?.map((portfolio) => (
                  <div key={portfolio.portfolio_id} className="min-w-[160px] md:min-w-0 flex-shrink-0 md:flex-shrink">
                    <PortfolioSummaryCard
                      portfolio={portfolio}
                      onClick={handlePortfolioClick}
                      isSelected={selectedPortfolioId === portfolio.portfolio_id}
                    />
                  </div>
                ))}
              </div>
            )}
          </section>
        </aside>

        {/* Right column — Sites */}
        <section className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-ems-text-primary">
                {selectedPortfolioId
                  ? portfolios?.find(p => p.portfolio_id === selectedPortfolioId)?.name || 'Portfolio'
                  : 'All Sites'}
              </h2>
              {sitesData && (
                <p className="text-sm text-ems-text-muted mt-0.5">
                  {sitesData.sites.length} of {sitesData.pagination.total} sites
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ems-text-muted pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search sites..."
                  className="w-64 pl-10 pr-3 py-2 bg-ems-bg border border-ems-border rounded-lg text-sm text-ems-text-primary placeholder:text-ems-text-muted focus:outline-none focus:border-ems-border-bright transition-colors"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              {selectedPortfolioId && (
                <button
                  onClick={() => setPortfolioFilter(undefined)}
                  className="px-3 py-1.5 bg-ems-bg hover:bg-ems-bg-card-hover border border-ems-border rounded-lg text-sm text-ems-text-secondary transition-colors"
                >
                  Clear Filter
                </button>
              )}
            </div>
          </div>
          <SiteGrid
            sites={sitesData?.sites || []}
            isLoading={sitesLoading}
            liveBySite={liveBySite}
            nowMs={nowMs}
            pagination={sitesData?.pagination}
            onSiteClick={handleSiteClick}
          />
        </section>
      </div>

      {/* Mobile: Sticky summary button */}
      {aggregateData && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 p-3 pointer-events-none z-30">
          <button
            onClick={() => setShowSummary(true)}
            className="w-full bg-ems-forest text-ems-text-primary border border-ems-border rounded-2xl py-3 px-4 font-semibold text-sm flex items-center justify-center gap-2 pointer-events-auto"
          >
            <ChevronUp className="w-4 h-4" />
            Portfolio Summary
          </button>
        </div>
      )}

      {/* Mobile: Bottom sheet */}
      {showSummary && aggregateData && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/30 animate-fade-in"
            onClick={() => setShowSummary(false)}
            aria-hidden="true"
          />
          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 max-h-[75vh] bg-ems-bg-card rounded-t-2xl animate-slide-up overflow-hidden flex flex-col">
            {/* Drag handle */}
            <div className="flex justify-center py-3 flex-shrink-0">
              <div className="w-9 h-1 bg-ems-text-muted rounded-full" />
            </div>
            {/* Scrollable content */}
            <div className="overflow-y-auto flex-1 px-1">
              <PortfolioSummaryPanel
                data={aggregateData}
                isLoading={isAggregateLoading}
                onClose={() => setShowSummary(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryItem({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="flex-shrink-0">
      <p className="text-xs text-ems-text-muted whitespace-nowrap">{label}</p>
      <p className={`text-base md:text-lg font-bold font-mono ${color || 'text-ems-text-primary'} whitespace-nowrap`}>{value}</p>
    </div>
  );
}
